import React, { useCallback, useMemo } from 'react';
import { isNull, isUndefined } from 'lodash/fp';

import { useAppDispatch, useAppSelector } from '../../../../hooks/react-redux';
import { PortfolioAnalyticsSurveyQuestion, getIsChangingQuestion, askPortfolioQuestionStarted, PortfolioQuestion, getDocumentFields, setSurveySummary, getPortfolioAnalyticsTemplateUsed } from '../store';
import { PortfolioAnalyticsSlider } from './PortfolioAnalyticsSlider';
import { DropdownOption } from '../../../shared/dropdown/Dropdown';
import { PortfolioAnalyticsSurveyQuestions } from './PortfolioAnalyticsSurveyQuestions';
import styles from '../PortfolioAnalytics.module.scss';
import { Button } from '../../../shared/button/Button';
import { getAllClientDatasets } from '../../client-datasets/store';
import { getAllDocumentNames } from '../../documents/store';
import { DatasetType } from '../../../datasets/store';

interface PortfolioAnalyticsSurveyProps {
    questionIndex: number;
    setQuestionIndex: (index: number) => void;
    portfolioQuestion: PortfolioQuestion;
    backToTemplates: () => void;
}

export const PortfolioAnalyticsSurvey: React.FC<PortfolioAnalyticsSurveyProps> = ({ questionIndex, setQuestionIndex, portfolioQuestion, backToTemplates }) => {

    const dispatch = useAppDispatch();

    const changingQuestion = useAppSelector(getIsChangingQuestion);
    const datasets = useAppSelector(getAllClientDatasets);
    const documentNames = useAppSelector(getAllDocumentNames);
    const availableFields = useAppSelector(getDocumentFields);
    const templateUsed = useAppSelector(getPortfolioAnalyticsTemplateUsed);
    const { question, parentDatasetId, fieldId, filters: { documentNameIds } } = portfolioQuestion;

    const selectedDataset = useMemo(() => datasets.find(({ datasetId }) => datasetId === parentDatasetId), [datasets, parentDatasetId]);
    const selectedDatasetOption: DropdownOption | null = useMemo(() => {
        if (!isUndefined(selectedDataset) && !isNull(parentDatasetId)) {
            return { value: parentDatasetId.toString(), label: selectedDataset.agreementType };
        }
        return null;
    }, [selectedDataset, parentDatasetId]);

    const datasetOptions = useMemo(() => datasets.map(({ datasetId, agreementType }) => ({ value: datasetId.toString(), label: agreementType })), [datasets]);

    const documentNameOptions = useMemo(() => documentNames.filter(({ datasetId }) => datasetId === parentDatasetId).map(({ documentNameId, documentName }) => ({ value: documentNameId!.toString(), label: documentName })), [documentNames, parentDatasetId]);
    const selectedDocumentNames = useMemo(() => documentNameOptions.filter(({ value }) => documentNameIds.includes(parseInt(value))), [documentNameOptions, documentNameIds]);

    const filteredFields = availableFields.filter(field => !(field.datasetType === DatasetType.TABLE && field.fieldIndex === 0));

    const fieldOptions = useMemo(() => filteredFields.map(({ id, label, datasetTitle }) => ({ value: id, label: `${datasetTitle}: ${label}` })), [filteredFields]);

    const selectedField = useMemo(() => {
        const field = !isNull(fieldId) ? filteredFields.find(({ id }) => id === fieldId) : undefined;
        if (!isUndefined(field)) {
            return { label: field.label, value: field.id };
        }
        return null;
    }, [filteredFields, fieldId]);

    const portfolioQuestionSurvey = useMemo(() => {
        let surveyQuestions: PortfolioAnalyticsSurveyQuestion[] = [
            { question: 'What Agreement Type would you like to ask a question about?', selectedOption: selectedDatasetOption, options: datasetOptions, key: 'parentDatasetId' },
            { question: 'Which Field would you like to ask a question about?', selectedOption: selectedField, options: fieldOptions, key: 'fieldId' },
            { question: 'What would you like to know about this field?', userQuestion: question, key: 'question' },
        ];
        return surveyQuestions;
    }, [selectedDatasetOption, datasetOptions, selectedField, fieldOptions, question]);

    const previousQuestion = useCallback(() => setQuestionIndex(questionIndex - 1), [questionIndex, setQuestionIndex]);
    const nextQuestion = useCallback(() => questionIndex !== portfolioQuestionSurvey.length - 1 && setQuestionIndex(questionIndex + 1), [questionIndex, portfolioQuestionSurvey, setQuestionIndex]);
    const canPrevious = useMemo(() => questionIndex !== 0, [questionIndex]);

    const canNext = useMemo(() => {
        return questionIndex !== 2 && !changingQuestion && !isNull(portfolioQuestionSurvey[questionIndex].selectedOption);
    }, [portfolioQuestionSurvey, questionIndex, changingQuestion]);

    const canGenerate = useMemo(() => !!(questionIndex === 2 && question), [questionIndex, question]);

    const summaryMeta = useMemo(() => {
        const agreementType = selectedDatasetOption?.label || '';
        const documentNames = selectedDocumentNames.map(({ label }) => label);
        const fieldLabel = selectedField?.label || '';
        return { agreementType, documentNames, fieldLabel, questionAsked: question };
    }, [selectedDatasetOption, selectedDocumentNames, selectedField, question]);

    const askPortfolioQuestion = useCallback(() => {
        dispatch(askPortfolioQuestionStarted(portfolioQuestion));
        dispatch(setSurveySummary(summaryMeta));
    }, [dispatch, portfolioQuestion, summaryMeta]);

    return (
        <div className={styles.survey}>
            <div className={styles.surveyWrapper}>
                <PortfolioAnalyticsSlider surveyQuestionIndex={questionIndex} setQuestionIndex={setQuestionIndex} portfolioAnalyticsSurvey={portfolioQuestionSurvey} />
                <PortfolioAnalyticsSurveyQuestions
                    surveyQuestionIndex={questionIndex}
                    setQuestionIndex={setQuestionIndex}
                    portfolioAnalyticsSurvey={portfolioQuestionSurvey}
                    changingQuestion={changingQuestion}
                    filteredFields={filteredFields}
                    askPortfolioQuestion={askPortfolioQuestion}
                    documentNames={documentNames}
                />
            </div>
            <div className={styles.surveyButtonWrapper}>
                <div className={styles.surveyButtonPrevious}>{questionIndex !== 0 && <Button label='Previous' onClick={previousQuestion} disabled={!canPrevious} />}</div>
                {!isNull(templateUsed) && <Button label='Back to Templates' onClick={backToTemplates} />}
                <div className={styles.surveyButtonNext}>
                    {canNext && <Button label='Next' onClick={nextQuestion} disabled={!canNext} />}
                    {questionIndex === 2 && <Button label='Generate Results' onClick={askPortfolioQuestion} disabled={!canGenerate} />}
                </div>
            </div>
        </div>
    );
};
